<template>
    <div v-if="isLoading">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <form v-else @submit.prevent="save">
        <div class="row my-3" v-if="hasEditableFields">
            <div class="col-12 text-right">
                <OmniButton
                    :isProcessing="isSavingSomething"
                    :disabled="isSavingSomething"
                    type="submit"
                    variant="success"
                    icon="save"
                    size="lg"
                >
                    {{ $t('omt.gui.general.action.save') }}
                </OmniButton>
            </div>
        </div>

        <b-card>
            <b-card-title class="text-uppercase">
              {{ $t('omt.gui.location-management.service-properties.title') }}
            </b-card-title>

            <div class="row my-2">
                <div class="col-3"><i>{{ $t('omt.gui.location-management.service-properties.label.property') }}</i></div>
                <div class="col-2"><i>{{ $t('omt.gui.location-management.service-properties.label.required') }}</i></div>
                <div class="col-1"><i>{{ $t('omt.gui.location-management.service-properties.label.type') }}</i></div>
                <div class="col-5"><i>{{ $t('omt.gui.location-management.service-properties.label.value') }}</i></div>
            </div>
            <div class="row my-3" v-for="property in locationProperties" :key="property.key.id">
                <div class="col-3" v-html="property.key.display_name" style="line-height:140%;"></div>
                <div class="col-1">
                    <span v-if="property.key.required">{{ $t('omt.gui.general.label.yes') }}</span>
                    <span v-else>{{ $t('omt.gui.general.label.no') }}</span>
                </div>
                <div class="col-2">{{ property.key.data_type | dataTypeTranslation }}</div>
                <div class="col-5">
                    <div v-if="property.key.identifier === 'storeImages'">
                        <input type="text" v-model.trim="property.value" class="form-control form-control-sm" :required="property.key.required === 1" :readonly="!property.can_edit"/>
                        <form id="uploadForm" v-if="property.can_edit" class="mt-1">
                            Foto uploaden <input type="file" id="storeImageFile" @change="uploadLocationImage($event, property)"/>
                        </form>
                    </div>
                    <div v-else-if="property.key.identifier === 'supportsSalesChannels'">
                        <omni-checkboxes :options="salesChannelOmniCheckboxOptions" v-model="property.value" :disabled="!property.can_edit"/>
                    </div>
                    <input v-else-if="property.key.data_type === 'int'" type="number" v-model="property.value" class="form-control form-control-sm" :required="property.key.required === 1"
                           :readonly="!property.can_edit"/>
                    <select v-else-if="property.key.data_type === 'boolean'" v-model="property.value" class="form-control form-control-sm" :required="property.key.required === 1"
                            :disabled="!property.can_edit">
                        <option value="1">{{ $t('omt.gui.general.label.yes') }}</option>
                        <option value="0">{{ $t('omt.gui.general.label.no') }}</option>
                    </select>
                    <textarea v-else-if="property.key.data_type === 'text'" v-model="property.value" class="form-control form-control-sm" :required="property.key.required === 1" :disabled="!property.can_edit"></textarea>
                    <input v-else type="text" v-model.trim="property.value" class="form-control form-control-sm" :required="property.key.required === 1" :readonly="!property.can_edit"/>
                </div>
            </div>
        </b-card>
        
        <div class="row my-3" v-if="hasEditableFields">
            <div class="col-12 text-right">
                <OmniButton
                    :isProcessing="isSavingSomething"
                    :disabled="isSavingSomething"
                    type="submit"
                    variant="success"
                    icon="save"
                    size="lg"
                >
                    {{ $t('omt.gui.general.action.save') }}
                </OmniButton>
            </div>
        </div>
    </form>
</template>

<script>

const DATA_TYPE_TRANSLATIONS = {
    int: 'Geheel getal',
    varchar: 'Tekst',
    text: 'Tekst (groot)',
    boolean: 'Ja / nee',
}

import _ from 'lodash'

export default {
    name: "EditLocationProperties",

    props: {
        location: {required: true, type: Object},
    },

    data() {
        return {
            isLoading: true,
            locationProperties: [],
            isSaving: {
                locationProperties: false,
            },
        }
    },

    mounted() {
        this.loadProperties()
            .then(() => this.isLoading = false)
    },

    methods: {
        loadProperties() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties`)
                .then(response => {
                    this.locationProperties = response.data.properties
                        .filter(property => {
                            return !property.key.identifier.startsWith('services.')
                                && !property.key.identifier.startsWith('syncServiceTimes.')
                        })
                        .map(property => {
                            if (property.value === null) {
                                property.value = ''
                            } else if (property.key.data_type === 'boolean') {
                                property.value = (property.value) ? '1' : '0'
                            }
                            return property
                        })
                })
        },

        uploadLocationImage(event, property) {
            const target = event.target;
            if (!target || !target.files) {
                this.$alert('Uw browser ondersteunt geen file upload', 'danger', true)
                return
            }

            const formData = new FormData();
            formData.append("image", target.files[0]);

            this.isSaving["locationProperties"] = true;
            this.$http.post(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties/${property.key.identifier}/image`, formData)
                .then(response => {
                    property.value = response.data.text_value
                    this.$alert('De foto is opgeslagen')
                })
                .catch(error => {
                    this.$alert('Er is iets misgegaan bij het opslaan van de foto', 'danger', true)
                })
                .finally(() => {
                    this.isSaving["locationProperties"] = false;
                })
        },

        save() {
            this.isSaving["locationProperties"] = true;

            const postData = {
                properties: _.cloneDeep(this.locationProperties)
                    .map(property => {
                        property.key = property.key.identifier
                        return property
                    }),
            }

            this.$http.patch(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/properties`, postData)
                .then(response => {
                    this.$alert('Instellingen opgeslagen')
                })
                .catch(error => {
                    if (process.env['APP_ENV'] !== 'production') {
                        console.error('EditLocationProperties.vue: save(): ', error);
                    }
                    
                    this.$alert('Er is iets misgegaan bij het opslaan', 'danger', true)
                })
                .finally(() => {
                    this.isSaving["locationProperties"] = false;
                })
        },
    },

    computed: {
       isSavingSomething() {
        return Object.values(this.isSaving).some((value) => (value === true));
       },
       salesChannelOmniCheckboxOptions() {
           return _.map(this.$store.state.config['sales-channels'] ?? [], sc => ({
               text: sc,
               value: sc,
           }))
       },
        hasEditableFields() {
            if (!this.hasPermission('locations.properties.edit')) {
                return false;
            }

            return this.locationProperties.some(property => property.can_edit)
        },
    },

    filters: {
        dataTypeTranslation(value) {
            return DATA_TYPE_TRANSLATIONS[value]
                ? DATA_TYPE_TRANSLATIONS[value]
                : value
        }
    },
}
</script>

<style scoped>
input[readonly], select[disabled] {
    background: inherit !important;
    border: none !important;
}
</style>
